<template>
  <div :class="$style.chatContainer" ref="chatContainer">
    <transition name="fade">
      <div v-if="!showBottomBox" :class="$style.initialWrapper">
        <b :class="$style.chatHeader">What can I help with?</b>
        <div :class="[$style.messageBox, $style.initial]" ref="messageBox">
          <input
            v-model="newMessage"
            @keyup.enter="sendMessage"
            placeholder="Message Hilo AI..."
          />
          <button @click="sendMessage">
            <img
              :class="$style.frameChild"
              alt=""
              src="/static/img/arrow-right.svg"
            />
          </button>
        </div>
      </div>
    </transition>

    <div :class="$style.messages" ref="messages">
      <div
        v-for="message in items"
        :key="message.id"
        :class="[
          $style.message,
          message.type === 'user' ? $style.userMessage : $style.botMessage,
        ]"
      >
        <div v-if="message.type === 'user'">
          <span>{{ message.text }}</span>
        </div>

        <div
          v-else-if="message.type === 'loading'"
          :class="$style.spinnerContainer"
        >
          <i class="fas fa-spinner fa-spin"></i>
        </div>

        <div v-else>
          <span>{{ message.text }}</span>
        </div>
      </div>
    </div>

    <transition name="fade">
      <div
        v-if="showBottomBox"
        :class="[$style.messageBox, $style.bottom]"
        ref="messageBox"
      >
        <input
          v-model="newMessage"
          @keyup.enter="sendMessage"
          placeholder="Message Hilo AI..."
        />
        <button @click="sendMessage">
          <img
            :class="$style.frameChild"
            alt=""
            src="/static/img/arrow-right.svg"
          />
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import dayjs from "dayjs";
import { Input, Button } from "element-ui";
import AIAPIService from "../../../servicehandlers/AIAPIService";

const aiApiService = new AIAPIService();

export default {
  components: {
    [Input.name]: Input,
    [Button.name]: Button,
  },
  data() {
    return {
      newMessage: "",
      messages: [],
      items: [],
      recentQuestions: JSON.parse(localStorage.getItem("recentQuestions")) || [],
      beginningText: true,
      send_context: false,
      context: "You are an expert in aviation. Answer the questions in a friendly manner.",
      spinning: false,
      isSending: false,
      showBottomBox: false,
    };
  },
  mounted() {
    this.generateRandomString();
    this.checkScrollBar();
    window.addEventListener("resize", this.checkScrollBar);
  },
  updated() {
    this.checkScrollBar();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkScrollBar);
  },
  watch: {
    items() {
      this.$nextTick(this.checkScrollBar);
    },
  },
  methods: {
    async sendMessage() {
      if (this.isSending || this.newMessage.trim() === "") return;

      this.beginningText = false;
      this.isSending = true;

      const userMessage = {
        id: this.items.length,
        text: this.newMessage.trim(),
        type: "user",
      };
      this.items.push(userMessage);
      this.storeRecentQuestion(this.newMessage.trim());

      this.newMessage = "";
      this.showBottomBox = true;

      this.scrollToBottom();

      const loadingMessage = {
        id: this.items.length,
        text: "",
        type: "loading",
      };
      this.items.push(loadingMessage);

      const message = {
        context: this.context,
        query: userMessage.text,
        key: this.randomUUID,
        is_query_response: false,
      };

      try {
        const response = await aiApiService.make_ai_call(message, this.$route);
        this.items.pop();  
        const botResponse = {
          id: this.items.length,
          text: response,
          type: "bot",
        };
        this.items.push(botResponse);
      } catch (error) {
        this.items.pop();
        this.handleError(`Error sending message: ${error}`);
      }

      this.scrollToBottom();
      this.isSending = false;
    },

    storeRecentQuestion(question) {
      const newQuestion = {
        id: this.recentQuestions.length,
        text: question,
        date: new Date().toISOString(),
      };

      this.recentQuestions = this.recentQuestions.filter(
        (q) => q.text !== question
      );
      this.recentQuestions.push(newQuestion);

      localStorage.setItem(
        "recentQuestions",
        JSON.stringify(this.recentQuestions)
      );
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messages; 
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    clearToday() {
      const today = dayjs().startOf("day");
      this.recentQuestions = this.recentQuestions.filter(
        (q) => !dayjs(q.date).isSame(today, "day")
      );
      localStorage.setItem("recentQuestions", JSON.stringify(this.recentQuestions));
    },

    clearPrevious() {
      const today = dayjs().startOf("day");
      this.recentQuestions = this.recentQuestions.filter(
        (q) => dayjs(q.date).isSame(today, "day")
      );
      localStorage.setItem("recentQuestions", JSON.stringify(this.recentQuestions));
    },

    clear() {
      this.items = [];
      this.beginningText = true;
    },

    reAskQuestion(questionText) {
      this.newMessage = questionText;
      this.sendMessage();
    },

    handleError(error) {
      this.spinning = false;
    },

    checkScrollBar() {
      const container = this.$refs.messages; 
      if (container) {
        if (container.scrollHeight > container.clientHeight) {
          container.classList.add("has-scrollbar");
        } else {
          container.classList.remove("has-scrollbar");
        }
      }
    },

    generateRandomString() {
      const length = 10;
      const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      let result = "";

      for (let i = 0; i < length; i++) {
        const randomIndex = Math.floor(Math.random() * characters.length);
        result += characters.charAt(randomIndex);
      }

      this.randomUUID = result;
    },
  },
};
</script>

<style module>
.chatHeader {
  font-size: 30px;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin-bottom: 15px;
  font-family: Helvetica, Arial, sans-serif;
  transition: opacity 0.3s ease-in-out;
  position: absolute;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  top: 38%;
}
.initialWrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
}
.chatContainer {
  display: flex;
  flex-direction: column;
  height: 80vh;
  justify-content: space-between;
  padding: 20px;
  box-sizing: border-box;
}
.messages {
  flex: 1;
  width: 100%;
  max-height: calc(100vh - 60px);
  overflow-y: auto;
  padding: 20px;
  box-sizing: border-box;
  margin: auto;
  width: 80%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.message {
  background: #f1f1f1;
  margin: 10px 0;
  padding: 12px 16px;
  border-radius: 8px;
  max-width: 80%;
  word-wrap: break-word;
}
.userMessage {
  align-self: flex-end;
  text-align: right;
  background-color: #e6e6e6;
  border-radius: 15px;
  max-width: 60%;
  font-size: 15px;
}
.botMessage {
  align-self: flex-start;
  text-align: left;
  background-color: lightgray;
  border-radius: 15px;
  max-width: 60%;
  font-size: 15px;
}
.spinnerContainer,
.spinnerContainer * {
  background: transparent;
  box-shadow: none;
  border: none;
}
.spinnerContainer i {
  font-size: 24px;
  color: #555;
  animation: spin 2s linear infinite;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.messageBox {
  position: absolute;
  display: flex;
  align-items: center;
  width: 741px;
  height: 50px;
  background: #e6e6e6;
  padding: 10px 10px 10px 10px;
  border-radius: 15px;
  transition: all 0.1s ease;
}
.messageBox input {
  flex: 1;
  border: none;
  outline: none;
  padding: 10px;
  font-size: 18px;
  background: transparent;
}
.messageBox button {
  border: none;
  background: #000;
  color: white;
  padding: 3px 5px;
  cursor: pointer;
  border-radius: 30px;
  font-size: 18px;
}
.messageBox button:hover {
  background: #555;
}
.initial {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.bottom {
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
}
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.5s ease;
}
.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}
.slide-down-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
.main-panel .content {
  padding: 0px !important;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.main-panel .content {
  background-color: white !important;
}
</style>
